export const initialResume = {
  photo: "",
  personal: {
    fullName: "",
    title: "",
    email: "",
    phone: "",
    location: "",
    website: "",
    linkedin: "",
    github: "",
  },
  summary: "",
  experience: [
    {
      id: crypto.randomUUID(),
      company: "",
      role: "",
      location: "",
      startDate: "",
      endDate: "",
      current: false,
      bullets: [""],
    },
  ],
  education: [
    {
      id: crypto.randomUUID(),
      school: "",
      degree: "",
      field: "",
      startDate: "",
      endDate: "",
      details: "",
    },
  ],
  skills: [],
  projects: [
    {
      id: crypto.randomUUID(),
      name: "",
      link: "",
      description: "",
      tech: "",
    },
  ],
  certifications: [
    {
      id: crypto.randomUUID(),
      name: "",
      issuer: "",
      date: "",
    },
  ],
  languages: [
    {
      id: crypto.randomUUID(),
      name: "",
      level: "",
    },
  ],
};

export const sampleResume = {
  photo: "",
  personal: {
    fullName: "Alex Chen",
    title: "Senior Product Designer",
    email: "alex.chen@example.com",
    phone: "+1 (415) 555-0132",
    location: "San Francisco, CA",
    website: "alexchen.design",
    linkedin: "linkedin.com/in/alexchen",
    github: "github.com/alexchen",
  },
  summary:
    "Senior product designer with 8+ years shipping consumer and enterprise software at scale. Led design systems adopted by 40+ engineers, and increased activation by 34% through iterative research and prototyping.",
  experience: [
    {
      id: crypto.randomUUID(),
      company: "Northwind Labs",
      role: "Senior Product Designer",
      location: "San Francisco, CA",
      startDate: "2022",
      endDate: "Present",
      current: true,
      bullets: [
        "Led end-to-end redesign of billing flow, lifting free-to-paid conversion by 27% across 1.2M monthly users.",
        "Established a 120-component design system in Figma adopted by 40+ engineers, cutting UI build time by 45%.",
        "Ran 60+ user interviews to shape a new onboarding pattern, reducing time-to-first-value from 9 to 3 minutes.",
      ],
    },
    {
      id: crypto.randomUUID(),
      company: "Meridian",
      role: "Product Designer",
      location: "Remote",
      startDate: "2019",
      endDate: "2022",
      current: false,
      bullets: [
        "Designed 4 shipped mobile features with combined DAU of 600k, including a share flow with 3.1M interactions/mo.",
        "Partnered with data science to A/B test 12 navigation experiments; winning variant became global default.",
      ],
    },
  ],
  education: [
    {
      id: crypto.randomUUID(),
      school: "Rhode Island School of Design",
      degree: "BFA",
      field: "Graphic Design",
      startDate: "2013",
      endDate: "2017",
      details: "Cum Laude. Thesis on typographic systems in digital products.",
    },
  ],
  skills: [
    "Figma",
    "Design Systems",
    "User Research",
    "Prototyping",
    "React",
    "HTML/CSS",
    "Accessibility",
    "Motion Design",
  ],
  projects: [
    {
      id: crypto.randomUUID(),
      name: "Type Foundry",
      link: "typefoundry.co",
      description:
        "Open source library of 40+ variable font pairings with live preview and code snippets. 4.2k GitHub stars.",
      tech: "Next.js, TypeScript",
    },
  ],
  certifications: [
    {
      id: crypto.randomUUID(),
      name: "Nielsen Norman UX Certification",
      issuer: "NN/g",
      date: "2021",
    },
  ],
  languages: [
    { id: crypto.randomUUID(), name: "English", level: "Native" },
    { id: crypto.randomUUID(), name: "Mandarin", level: "Conversational" },
  ],
};

// ----------------------- ROLE-BASED SAMPLE RESUMES -----------------------
// Small helper: strip whitespace and generate stable IDs at load time.
const withId = (obj) => ({ ...obj, id: crypto.randomUUID() });

const studentSample = {
  photo: "",
  personal: {
    fullName: "Priya Sharma",
    title: "Computer Science Undergraduate",
    email: "priya.sharma@iitkgp.ac.in",
    phone: "+91 98450 12345",
    location: "Kharagpur, India",
    website: "priyasharma.dev",
    linkedin: "linkedin.com/in/priyasharma",
    github: "github.com/priyasharma",
  },
  summary:
    "Third-year CS undergraduate at IIT Kharagpur with a strong foundation in data structures, algorithms, and full-stack web development. Interested in backend systems and applied ML. Winner of two national-level hackathons.",
  experience: [
    withId({
      company: "Cognizant",
      role: "Software Engineering Intern",
      location: "Remote",
      startDate: "May 2025",
      endDate: "Jul 2025",
      current: false,
      bullets: [
        "Built a customer-facing analytics dashboard in React + FastAPI serving 25k internal users, reducing report-generation time from 3 minutes to 8 seconds.",
        "Wrote 90+ pytest cases raising module test coverage from 42% to 87% and caught 6 pre-production bugs.",
        "Collaborated with 5 engineers using Git, JIRA, and daily stand-ups in an Agile Scrum cadence.",
      ],
    }),
    withId({
      company: "IIT Kharagpur — Prof. R. Banerjee's Lab",
      role: "Undergraduate Research Assistant",
      location: "Kharagpur, India",
      startDate: "Jan 2025",
      endDate: "Present",
      current: true,
      bullets: [
        "Implemented a graph neural network in PyTorch for social-network link prediction, improving F1 by 6.2 points over the baseline.",
        "Co-authored a paper submitted to ICLR Workshop 2026 on scalable subgraph sampling.",
      ],
    }),
  ],
  education: [
    withId({
      school: "Indian Institute of Technology, Kharagpur",
      degree: "BTech",
      field: "Computer Science and Engineering",
      startDate: "2023",
      endDate: "2027 (expected)",
      details: "CGPA 9.24/10. Coursework: DSA, OS, DBMS, Machine Learning, Distributed Systems.",
    }),
    withId({
      school: "Delhi Public School, R.K. Puram",
      degree: "Higher Secondary (Class XII)",
      field: "Science (PCM + CS)",
      startDate: "2021",
      endDate: "2023",
      details: "97.6% aggregate. School topper in Computer Science.",
    }),
  ],
  skills: [
    "Python",
    "C++",
    "Java",
    "JavaScript",
    "React",
    "FastAPI",
    "PostgreSQL",
    "Git",
    "Data Structures",
    "Algorithms",
    "PyTorch",
  ],
  projects: [
    withId({
      name: "CampusRide",
      link: "github.com/priyasharma/campusride",
      description:
        "A ride-sharing platform for IIT Kharagpur students with real-time matching and Razorpay payments. 1.4k active users on campus.",
      tech: "React, FastAPI, PostgreSQL, WebSockets",
    }),
    withId({
      name: "Leetcode Streak Tracker",
      link: "streaks.priyasharma.dev",
      description:
        "Chrome extension that gamifies daily LeetCode practice with streaks, XP, and friend leaderboards. 3,200+ installs on the Chrome Web Store.",
      tech: "TypeScript, Chrome Extension APIs, Firebase",
    }),
  ],
  certifications: [
    withId({ name: "AWS Certified Cloud Practitioner", issuer: "Amazon Web Services", date: "2025" }),
    withId({ name: "Deep Learning Specialization", issuer: "DeepLearning.AI / Coursera", date: "2024" }),
  ],
  languages: [
    withId({ name: "English", level: "Fluent" }),
    withId({ name: "Hindi", level: "Native" }),
    withId({ name: "Bengali", level: "Conversational" }),
  ],
};

const doctorSample = {
  photo: "",
  personal: {
    fullName: "Dr. Anita Desai",
    title: "Internal Medicine Physician",
    email: "anita.desai@memorial.org",
    phone: "+1 (617) 555-0198",
    location: "Boston, MA",
    website: "",
    linkedin: "linkedin.com/in/dranitadesai",
    github: "",
  },
  summary:
    "Board-certified internal medicine physician with 9+ years of inpatient and outpatient care experience. Managed a panel of 1,800+ primary-care patients, led a diabetes-improvement initiative that lifted HbA1c control by 22%, and mentored 30+ residents.",
  experience: [
    withId({
      company: "Memorial General Hospital",
      role: "Attending Physician, Internal Medicine",
      location: "Boston, MA",
      startDate: "2021",
      endDate: "Present",
      current: true,
      bullets: [
        "Manage inpatient service of 18–24 beds covering complex multi-comorbidity cases; consistently rated in the top 10% for length-of-stay efficiency and readmission avoidance.",
        "Led a quality-improvement project on diabetes care that raised HbA1c-under-7% rates from 41% to 63% across a 1,800-patient panel.",
        "Precept 4 rotating internal-medicine residents per block; recognized twice with the Chief-of-Medicine Teaching Award (2023, 2024).",
      ],
    }),
    withId({
      company: "Brigham Community Clinic",
      role: "Primary Care Physician",
      location: "Cambridge, MA",
      startDate: "2018",
      endDate: "2021",
      current: false,
      bullets: [
        "Provided longitudinal primary care to an underserved patient population of 1,200; expanded same-day access from 14% to 38%.",
        "Piloted an SMS-based reminder system that cut no-show rates by 31%.",
      ],
    }),
  ],
  education: [
    withId({
      school: "Harvard Medical School",
      degree: "MD",
      field: "Doctor of Medicine",
      startDate: "2011",
      endDate: "2015",
      details: "Alpha Omega Alpha Honor Society. Dean's List for scholarly research.",
    }),
    withId({
      school: "Massachusetts General Hospital",
      degree: "Residency",
      field: "Internal Medicine",
      startDate: "2015",
      endDate: "2018",
      details: "Chief Resident 2017–2018.",
    }),
    withId({
      school: "Stanford University",
      degree: "BS",
      field: "Human Biology (Honors)",
      startDate: "2007",
      endDate: "2011",
      details: "Phi Beta Kappa. GPA 3.94/4.0.",
    }),
  ],
  skills: [
    "Adult Primary Care",
    "Inpatient Management",
    "Diabetes Care",
    "Hypertension",
    "Chronic Disease Management",
    "EHR (Epic)",
    "Point-of-Care Ultrasound",
    "Clinical Teaching",
    "Quality Improvement",
  ],
  projects: [
    withId({
      name: "Diabetes Care Redesign",
      link: "",
      description:
        "Led a multi-disciplinary care redesign for the internal-medicine clinic's 320 diabetic patients, achieving a 22-point improvement in HbA1c control. Findings presented at SGIM National Meeting 2024.",
      tech: "Root-cause analysis, PDSA cycles, EHR dashboards",
    }),
  ],
  certifications: [
    withId({ name: "Board Certification — Internal Medicine (ABIM)", issuer: "American Board of Internal Medicine", date: "2018 (recert 2028)" }),
    withId({ name: "Advanced Cardiac Life Support (ACLS)", issuer: "American Heart Association", date: "2024" }),
    withId({ name: "Basic Life Support (BLS)", issuer: "American Heart Association", date: "2024" }),
  ],
  languages: [
    withId({ name: "English", level: "Native" }),
    withId({ name: "Hindi", level: "Fluent" }),
    withId({ name: "Gujarati", level: "Conversational" }),
  ],
};

const salesManagerSample = {
  photo: "",
  personal: {
    fullName: "Marcus Reilly",
    title: "Enterprise Software Sales Manager",
    email: "marcus.reilly@example.com",
    phone: "+1 (312) 555-0175",
    location: "Chicago, IL",
    website: "",
    linkedin: "linkedin.com/in/marcusreilly",
    github: "",
  },
  summary:
    "Enterprise software sales leader with 10+ years selling SaaS platforms to F500 accounts. Consistently exceeds quota (avg. 128% attainment over 5 years). Built and coached a team of 8 AEs to close $22M in new ARR last year.",
  experience: [
    withId({
      company: "Datastream Cloud",
      role: "Regional Sales Manager, Midwest",
      location: "Chicago, IL",
      startDate: "2022",
      endDate: "Present",
      current: true,
      bullets: [
        "Lead an 8-person AE team selling a $60k–$400k ACV data-platform SaaS to F1000 enterprises; team closed $22M new ARR in FY24 vs. $16M plan (138% attainment).",
        "Rebuilt the outbound motion around ICP-scored account tiering and multi-thread engagement, cutting sales cycle from 172 to 118 days.",
        "Personally closed 6 flagship logos including two 7-figure multi-year deals with Fortune 100 accounts.",
      ],
    }),
    withId({
      company: "Verta Analytics",
      role: "Senior Account Executive",
      location: "Chicago, IL",
      startDate: "2018",
      endDate: "2022",
      current: false,
      bullets: [
        "Closed $8.4M in new ARR across 4 years, hitting 142%, 118%, 131%, and 156% of quota respectively.",
        "Championed MEDDPICC adoption across the sales org, raising forecast accuracy from 61% to 89% within two quarters.",
        "Built and expanded the Ford, Kraft Heinz, and Walgreens accounts from initial pilot to $2M+ annual spend each.",
      ],
    }),
    withId({
      company: "Oracle",
      role: "Account Executive, ERP Cloud",
      location: "Chicago, IL",
      startDate: "2014",
      endDate: "2018",
      current: false,
      bullets: [
        "Sold ERP Cloud subscriptions to mid-market manufacturing accounts; achieved President's Club (top 5%) in 2016 and 2017.",
        "Averaged 121% of quota over 4 years with an average deal size of $180k ACV.",
      ],
    }),
  ],
  education: [
    withId({
      school: "Northwestern University — Kellogg School of Management",
      degree: "MBA",
      field: "Marketing & Strategy",
      startDate: "2012",
      endDate: "2014",
      details: "Kellogg Sales Institute Fellow.",
    }),
    withId({
      school: "University of Illinois Urbana-Champaign",
      degree: "BS",
      field: "Business Administration",
      startDate: "2007",
      endDate: "2011",
      details: "Dean's List all 8 semesters.",
    }),
  ],
  skills: [
    "Enterprise SaaS Sales",
    "MEDDPICC",
    "Value Selling",
    "Sales Coaching",
    "Pipeline Management",
    "Salesforce",
    "Gong",
    "Outreach",
    "Forecasting",
    "Territory Planning",
    "Executive Selling",
    "Negotiation",
  ],
  projects: [
    withId({
      name: "Midwest Playbook Refresh",
      link: "",
      description:
        "Rebuilt the Midwest team's discovery + demo playbook and rolled it out via 6 workshops. Win-rate on qualified opportunities jumped from 24% to 33% in two quarters.",
      tech: "Gong call analysis, Salesforce dashboards, MEDDPICC scoring",
    }),
  ],
  certifications: [
    withId({ name: "MEDDPICC Certified", issuer: "MEDDIC Academy", date: "2023" }),
    withId({ name: "Challenger Sale Practitioner", issuer: "CEB / Gartner", date: "2021" }),
    withId({ name: "Salesforce Administrator", issuer: "Salesforce", date: "2019" }),
  ],
  languages: [
    withId({ name: "English", level: "Native" }),
    withId({ name: "Spanish", level: "Conversational" }),
  ],
};

const accountantSample = {
  photo: "",
  personal: {
    fullName: "Rahul Menon",
    title: "Senior Accountant, CPA",
    email: "rahul.menon@example.com",
    phone: "+1 (646) 555-0210",
    location: "New York, NY",
    website: "",
    linkedin: "linkedin.com/in/rahulmenon-cpa",
    github: "",
  },
  summary:
    "CPA with 7 years of progressive experience in general ledger accounting, month-end close, and audit for public tech companies. Cut month-end close from 9 days to 4 by automating recurring journals and reconciliations in NetSuite.",
  experience: [
    withId({
      company: "Brightform Software (NASDAQ: BRFM)",
      role: "Senior Accountant",
      location: "New York, NY",
      startDate: "2022",
      endDate: "Present",
      current: true,
      bullets: [
        "Own the general-ledger close for a $340M-revenue SaaS business; reduced close from 9 to 4 business days by automating 62 recurring JEs in NetSuite.",
        "Prepared quarterly SOX-compliant journal-entry documentation and revenue-reconciliation schedules with zero material findings across 6 audits.",
        "Led implementation of a new revenue-recognition rule (ASC 606) for a new usage-based product line, coordinating across FP&A, legal, and Deloitte.",
      ],
    }),
    withId({
      company: "Ernst & Young LLP",
      role: "Audit Senior",
      location: "New York, NY",
      startDate: "2019",
      endDate: "2022",
      current: false,
      bullets: [
        "Led audit fieldwork for 3 SEC-registrant tech clients ($100M–$1.2B revenue), managing teams of 4–6 juniors.",
        "Delivered clean opinions on all engagements; identified $3.2M in previously mis-classified capitalized software costs.",
      ],
    }),
    withId({
      company: "KPMG",
      role: "Audit Associate",
      location: "New York, NY",
      startDate: "2017",
      endDate: "2019",
      current: false,
      bullets: [
        "Executed substantive testing on cash, AR, and revenue cycles for 6 mid-market clients under US GAAP.",
      ],
    }),
  ],
  education: [
    withId({
      school: "New York University — Stern School of Business",
      degree: "BS",
      field: "Accounting",
      startDate: "2013",
      endDate: "2017",
      details: "Beta Alpha Psi. Graduated summa cum laude, GPA 3.91/4.0.",
    }),
  ],
  skills: [
    "US GAAP",
    "ASC 606 Revenue Recognition",
    "Month-End Close",
    "General Ledger",
    "Reconciliations",
    "NetSuite",
    "SAP",
    "Excel (advanced)",
    "SOX 404 Controls",
    "External Audit",
    "Financial Reporting",
    "Variance Analysis",
  ],
  projects: [
    withId({
      name: "Close Automation Program",
      link: "",
      description:
        "Owned end-to-end automation of the monthly close using NetSuite scheduled scripts and BlackLine reconciliations. Saved the team ~140 hours per quarter.",
      tech: "NetSuite SuiteScript, BlackLine, Excel Power Query",
    }),
  ],
  certifications: [
    withId({ name: "Certified Public Accountant (CPA)", issuer: "New York State Board of Accountancy", date: "2019" }),
    withId({ name: "NetSuite Financial User Certification", issuer: "Oracle NetSuite", date: "2023" }),
  ],
  languages: [
    withId({ name: "English", level: "Fluent" }),
    withId({ name: "Malayalam", level: "Native" }),
    withId({ name: "Hindi", level: "Conversational" }),
  ],
};

const softwareEngineerSample = {
  photo: "",
  personal: {
    fullName: "Jordan Patel",
    title: "Senior Software Engineer",
    email: "jordan.patel@example.com",
    phone: "+1 (206) 555-0141",
    location: "Seattle, WA",
    website: "jordanpatel.dev",
    linkedin: "linkedin.com/in/jordanpatel",
    github: "github.com/jordanpatel",
  },
  summary:
    "Backend-leaning full-stack engineer with 8 years of experience building distributed systems that scale. Cut p99 latency on a 2M-RPS payments service by 42%. Comfortable across Go, Python, TypeScript, and Kubernetes.",
  experience: [
    withId({
      company: "Stripe",
      role: "Senior Software Engineer, Payments Platform",
      location: "Seattle, WA",
      startDate: "2022",
      endDate: "Present",
      current: true,
      bullets: [
        "Rewrote the card-authorization hot path in Go, reducing p99 latency from 380ms to 220ms across 2M RPS at peak with zero customer-facing incidents.",
        "Designed and shipped a Kafka-based idempotency layer that eliminated a class of duplicate-charge bugs affecting ~0.02% of retried requests.",
        "Mentored 4 mid-level engineers; two were promoted within 18 months.",
      ],
    }),
    withId({
      company: "Amazon Web Services — Lambda",
      role: "Software Engineer II",
      location: "Seattle, WA",
      startDate: "2019",
      endDate: "2022",
      current: false,
      bullets: [
        "Implemented cold-start optimization for Node.js runtimes on Lambda, reducing p50 cold-start from 340ms to 190ms for ~40% of invocations.",
        "Owned on-call for a service with a 99.99% availability SLA for 3 years; led 6 post-incident reviews.",
      ],
    }),
    withId({
      company: "Redfin",
      role: "Software Engineer I",
      location: "Seattle, WA",
      startDate: "2017",
      endDate: "2019",
      current: false,
      bullets: [
        "Built search-relevance A/B testing framework in Python + Elasticsearch that ran 40+ concurrent experiments.",
      ],
    }),
  ],
  education: [
    withId({
      school: "University of Washington",
      degree: "BS",
      field: "Computer Science",
      startDate: "2013",
      endDate: "2017",
      details: "Magna cum laude. Focus on distributed systems and databases.",
    }),
  ],
  skills: [
    "Go",
    "Python",
    "TypeScript",
    "Kubernetes",
    "Kafka",
    "PostgreSQL",
    "Redis",
    "gRPC",
    "AWS",
    "Terraform",
    "Distributed Systems",
    "Observability",
    "System Design",
  ],
  projects: [
    withId({
      name: "kctx",
      link: "github.com/jordanpatel/kctx",
      description:
        "Open-source Go CLI for fast kubectl context/namespace switching with fuzzy search. 2.8k GitHub stars, packaged in Homebrew.",
      tech: "Go, cobra, fzf",
    }),
    withId({
      name: "Latency Lab",
      link: "latencylab.jordanpatel.dev",
      description:
        "Interactive playground that visualises tail latency across simulated microservice topologies. Featured in Increment magazine.",
      tech: "Next.js, D3, WebAssembly (Rust)",
    }),
  ],
  certifications: [
    withId({ name: "Certified Kubernetes Administrator (CKA)", issuer: "The Linux Foundation", date: "2023" }),
    withId({ name: "AWS Solutions Architect — Associate", issuer: "Amazon Web Services", date: "2021" }),
  ],
  languages: [
    withId({ name: "English", level: "Native" }),
    withId({ name: "Gujarati", level: "Fluent" }),
    withId({ name: "Spanish", level: "Basic" }),
  ],
};

// Catalog exposed to the Builder UI — ordered as shown in the picker.
export const RESUME_SAMPLES = {
  student: studentSample,
  doctor: doctorSample,
  "sales-manager": salesManagerSample,
  accountant: accountantSample,
  "software-engineer": softwareEngineerSample,
};

export const SAMPLE_META = [
  { id: "student", label: "CS Student", description: "Third-year CS undergrad — internships, projects, coursework." },
  { id: "doctor", label: "Doctor", description: "Internal-medicine physician with clinical + teaching outcomes." },
  { id: "sales-manager", label: "Sales Manager", description: "Enterprise software sales leader with quota attainment." },
  { id: "accountant", label: "Accountant", description: "Senior accountant / CPA with close-cycle and audit results." },
  { id: "software-engineer", label: "Software Engineer", description: "Backend-heavy full-stack senior engineer." },
];
